export const getLISIQuery = (queryName, start, stop) => {
  return {
    hit_pieces_number: `
      from(bucket: "engine")
        |> range(start: ${start}, stop: ${stop})
        |> filter(fn: (r) => r["_measurement"] == "events")
        |> filter(fn: (r) => r["_field"] == "subtype")
        |> filter(fn: (r) => r["_value"] == 400)
        |> count()
    `,
    exceeded_oven_time_number: `
      from(bucket: "engine")
        |> range(start: ${start}, stop: ${stop})
        |> filter(fn: (r) => r["_measurement"] == "events")
        |> filter(fn: (r) => r["_field"] == "data_exceeded_oven_time")
        |> filter(fn: (r) => r["_value"] == 1)
        |> count()
    `,
    mean_oven_time: `
      from(bucket: "engine")
        |> range(start: ${start}, stop: ${stop})
        |> filter(fn: (r) => r["_measurement"] == "events")
        |> filter(fn: (r) => r["_field"] == "data_oven_time")
        |> mean()
    `,
    mean_transfer_time: `
      from(bucket: "engine")
        |> range(start: ${start}, stop: ${stop})
        |> filter(fn: (r) => r["_measurement"] == "events")
        |> filter(fn: (r) => r["_field"] == "data_transfer_time")
        |> mean()
    `,
    global_kpi: `
      import "math"

      bucketName = "engine"
      measurementName = "events"
      
      // Utilise uniquement par production_time_ratio.
      binSize = 15m
      
      // ========================================================================== 
      // KPI OPERATION + CONFIGURATION - Requete unique en sortie wide
      //
      // Une ligne par combinaison observee :
      // - data_pattern_operation + data_pattern_execution_configuration
      // - data_pattern_operation + configuration null = total de l'operation, toutes configurations confondues
      // - operation null + data_pattern_execution_configuration = total de la configuration, toutes operations confondues
      // - operation null + configuration null = total global sur pieces matchees uniquement
      // ========================================================================== 
      
      piecesNumeric =
        from(bucket: bucketName)
          |> range(start: ${start}, stop: ${stop})
          |> filter(fn: (r) => r["_measurement"] == measurementName)
          |> filter(fn: (r) =>
            r["_field"] == "data_oven_time" or
            r["_field"] == "data_transfer_time" or
            r["_field"] == "data_exceeded_oven_time" or
            r["_field"] == "data_pattern_oven_time" or
            r["_field"] == "data_pattern_oven_tolerance"
          )
          |> toFloat()
      
      piecesNonNumeric =
        from(bucket: bucketName)
          |> range(start: ${start}, stop: ${stop})
          |> filter(fn: (r) => r["_measurement"] == measurementName)
          |> filter(fn: (r) =>
            r["_field"] == "data_piece_id" or
            r["_field"] == "data_pattern_execution_id" or
            r["_field"] == "data_pattern_operation" or
            r["_field"] == "data_pattern_execution_configuration"
          )
      
      pieces =
        union(tables: [piecesNumeric, piecesNonNumeric])
          |> pivot(
            rowKey: ["_time", "workstation_id"],
            columnKey: ["_field"],
            valueColumn: "_value"
          )
          |> filter(fn: (r) => exists r.data_piece_id)
      
      piecesWithOperation =
        pieces
          |> filter(fn: (r) =>
            exists r.data_pattern_operation and
            r.data_pattern_operation != "None"
          )
      
      piecesWithConfiguration =
        pieces
          |> filter(fn: (r) =>
            exists r.data_pattern_execution_configuration and
            r.data_pattern_execution_configuration != "None"
          )
      
      piecesWithDimensions =
        pieces
          |> filter(fn: (r) =>
            exists r.data_pattern_operation and
            r.data_pattern_operation != "None" and
            exists r.data_pattern_execution_configuration and
            r.data_pattern_execution_configuration != "None"
          )
      
      piecesWithDimensionsAndExecution =
        piecesWithDimensions
          |> filter(fn: (r) => exists r.data_pattern_execution_id)
      
      piecesWithOperationAndExecution =
        piecesWithOperation
          |> filter(fn: (r) => exists r.data_pattern_execution_id)
      
      piecesWithConfigurationAndExecution =
        piecesWithConfiguration
          |> filter(fn: (r) => exists r.data_pattern_execution_id)
      
      piecesWithExecution =
        pieces
          |> filter(fn: (r) => exists r.data_pattern_execution_id)
      
      // --------------------------------------------------------------------------
      // Niveau operation + configuration
      // --------------------------------------------------------------------------
      
      struckPiecesCountByCombination =
        piecesWithDimensionsAndExecution
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "struck_pieces_count",
              _value: float(v: r.data_piece_id),
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      conformityRatioByCombination =
        piecesWithDimensionsAndExecution
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              total_piece: 1,
              conform_piece:
                if exists r.data_oven_time and exists r.data_pattern_oven_time and exists r.data_pattern_oven_tolerance then
                  if r.data_oven_time >= r.data_pattern_oven_time and r.data_oven_time <= r.data_pattern_oven_time + r.data_pattern_oven_tolerance then 1 else 0
                else 0
          }))
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> reduce(
              identity: {total_pieces: 0, conform_pieces: 0},
              fn: (r, accumulator) => ({
                total_pieces: accumulator.total_pieces + r.total_piece,
                conform_pieces: accumulator.conform_pieces + r.conform_piece
              })
          )
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "conformity_ratio",
              _value: if r.total_pieces == 0 then 0.0 else float(v: r.conform_pieces) / float(v: r.total_pieces),
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      exceededOvenTimeCountByCombination =
        piecesWithDimensionsAndExecution
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              exceeded_oven_piece: if exists r.data_exceeded_oven_time then (if r.data_exceeded_oven_time == 1.0 then 1 else 0) else 0
          }))
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> sum(column: "exceeded_oven_piece")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "exceeded_oven_time_count",
              _value: float(v: r.exceeded_oven_piece),
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      ovenTimeMeanByCombination =
        piecesWithDimensionsAndExecution
          |> filter(fn: (r) => exists r.data_oven_time)
          |> map(fn: (r) => ({
              _time: r._time,
              _value: r.data_oven_time,
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> mean(column: "_value")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "oven_time_mean",
              _value: r._value,
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      transferTimeMeanByCombination =
        piecesWithDimensionsAndExecution
          |> filter(fn: (r) => exists r.data_transfer_time)
          |> map(fn: (r) => ({
              _time: r._time,
              _value: r.data_transfer_time,
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> mean(column: "_value")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "transfer_time_mean",
              _value: r._value,
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      
      // --------------------------------------------------------------------------
      // Nouveau principe production_time_ratio
      // --------------------------------------------------------------------------
      // Numerateur = bins de production contenant au moins une piece,
      //              avec ou sans pattern, sur toute la plage/journee.
      // Denominateur = total des bins de la plage/journee.
      // Le ratio est global et recopie sur chaque combinaison pour conserver
      // une ligne par operation/configuration.
      
      productionTimeRatioGlobal =
        pieces
          |> map(fn: (r) => ({_time: r._time, _value: 1.0, join_key: "total"}))
          |> group(columns: ["join_key"])
          |> aggregateWindow(every: binSize, fn: count, createEmpty: true)
          |> reduce(
              identity: {prod_bins: 0.0, reference_bins: 0.0},
              fn: (r, accumulator) => ({
                prod_bins: accumulator.prod_bins + (if r._value > 0 then 1.0 else 0.0),
                reference_bins: accumulator.reference_bins + 1.0
              })
          )
          |> map(fn: (r) => ({
              join_key: "total",
              production_time_ratio: if r.reference_bins <= 0.0 then 0.0 else r.prod_bins / r.reference_bins,
              prod_bins: r.prod_bins,
              reference_bins: r.reference_bins
          }))
          |> keep(columns: ["join_key", "production_time_ratio", "prod_bins", "reference_bins"])
      
      
      productionRatioCombinationProdBins =
        piecesWithDimensionsAndExecution
          |> map(fn: (r) => ({
              _time: r._time,
              _value: 1.0,
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> aggregateWindow(every: binSize, fn: sum, createEmpty: false)
          |> reduce(
              identity: {prod_bins: 0.0},
              fn: (r, accumulator) => ({prod_bins: accumulator.prod_bins + 1.0})
          )
          |> keep(columns: ["data_pattern_operation", "data_pattern_execution_configuration", "prod_bins"])
      
      productionRatioCombinationActiveBins =
        piecesWithDimensionsAndExecution
          |> group(columns: ["data_pattern_execution_id", "data_pattern_operation", "data_pattern_execution_configuration"])
          |> sort(columns: ["_time"])
          |> reduce(
              identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
              fn: (r, accumulator) => ({
                points: accumulator.points + 1,
                first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
                last_ns: uint(v: r._time)
              })
          )
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              active_bins: if r.points == 0 then 0.0 else math.ceil(x: float(v: r.last_ns - r.first_ns) / float(v: uint(v: binSize)) + 1.0)
          }))
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> sum(column: "active_bins")
          |> keep(columns: ["data_pattern_operation", "data_pattern_execution_configuration", "active_bins"])
      
      productionTimeRatioCombinationKeys =
        piecesWithDimensions
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              join_key: "total",
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["join_key", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      productionTimeRatioByCombination =
        join(tables: {keys: productionTimeRatioCombinationKeys, ratio: productionTimeRatioGlobal}, on: ["join_key"])
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "production_time_ratio",
              _value: r.production_time_ratio,
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      meanSurplusOvenTimeVsPatternByCombination =
        piecesWithDimensionsAndExecution
          |> filter(fn: (r) =>
            exists r.data_oven_time and
            exists r.data_pattern_oven_time
          )
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              surplus_oven_time: if r.data_oven_time > r.data_pattern_oven_time then r.data_oven_time - r.data_pattern_oven_time else 0.0
          }))
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> mean(column: "surplus_oven_time")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "mean_surplus_oven_time_vs_pattern",
              _value: r.surplus_oven_time,
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      cadenceCombinationPieceCounts =
        piecesWithDimensionsAndExecution
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              total_pieces: float(v: r.data_piece_id)
          }))
          |> keep(columns: ["data_pattern_operation", "data_pattern_execution_configuration", "total_pieces"])
      
      cadenceCombinationActiveMinutes =
        piecesWithDimensionsAndExecution
          |> group(columns: ["data_pattern_execution_id", "data_pattern_operation", "data_pattern_execution_configuration"])
          |> sort(columns: ["_time"])
          |> reduce(
              identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
              fn: (r, accumulator) => ({
                points: accumulator.points + 1,
                first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
                last_ns: uint(v: r._time)
              })
          )
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              active_minutes: if r.points == 0 then 0.0 else math.ceil(x: float(v: r.last_ns - r.first_ns) / 60000000000.0 + 1.0)
          }))
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> sum(column: "active_minutes")
          |> keep(columns: ["data_pattern_operation", "data_pattern_execution_configuration", "active_minutes"])
      
      averagePiecesPerMinuteByCombination =
        join(tables: {pieces: cadenceCombinationPieceCounts, active: cadenceCombinationActiveMinutes}, on: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "average_pieces_per_minute",
              _value: if r.active_minutes <= 0.0 then 0.0 else r.total_pieces / r.active_minutes,
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      // Donnees de couverture calculees avec toutes les pieces du meme niveau,
      // y compris les pieces sans pattern lorsque ce niveau peut les porter.
      totalPiecesCountByCombination =
        piecesWithDimensions
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "total_pieces_count",
              _value: float(v: r.data_piece_id),
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      patternMatchedRatioByCombination =
        piecesWithDimensions
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              total_piece: 1,
              matched_piece: if exists r.data_pattern_execution_id then 1 else 0
          }))
          |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
          |> reduce(
              identity: {total_pieces: 0, matched_pieces: 0},
              fn: (r, accumulator) => ({
                total_pieces: accumulator.total_pieces + r.total_piece,
                matched_pieces: accumulator.matched_pieces + r.matched_piece
              })
          )
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "pattern_matched_ratio",
              _value: if r.total_pieces == 0 then 0.0 else float(v: r.matched_pieces) / float(v: r.total_pieces),
              data_pattern_operation: r.data_pattern_operation,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation", "data_pattern_execution_configuration"])
      
      // --------------------------------------------------------------------------
      // Niveau operation seule : configuration null
      // --------------------------------------------------------------------------
      
      struckPiecesCountByOperationRollup =
        piecesWithOperationAndExecution
          |> group(columns: ["data_pattern_operation"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "struck_pieces_count",
              _value: float(v: r.data_piece_id),
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      
      conformityRatioByOperationRollup =
        piecesWithOperationAndExecution
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              total_piece: 1,
              conform_piece:
                if exists r.data_oven_time and exists r.data_pattern_oven_time and exists r.data_pattern_oven_tolerance then
                  if r.data_oven_time >= r.data_pattern_oven_time and r.data_oven_time <= r.data_pattern_oven_time + r.data_pattern_oven_tolerance then 1 else 0
                else 0
          }))
          |> group(columns: ["data_pattern_operation"])
          |> reduce(
              identity: {total_pieces: 0, conform_pieces: 0},
              fn: (r, accumulator) => ({
                total_pieces: accumulator.total_pieces + r.total_piece,
                conform_pieces: accumulator.conform_pieces + r.conform_piece
              })
          )
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "conformity_ratio",
              _value: if r.total_pieces == 0 then 0.0 else float(v: r.conform_pieces) / float(v: r.total_pieces),
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      
      exceededOvenTimeCountByOperationRollup =
        piecesWithOperationAndExecution
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              exceeded_oven_piece: if exists r.data_exceeded_oven_time then (if r.data_exceeded_oven_time == 1.0 then 1 else 0) else 0
          }))
          |> group(columns: ["data_pattern_operation"])
          |> sum(column: "exceeded_oven_piece")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "exceeded_oven_time_count",
              _value: float(v: r.exceeded_oven_piece),
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      
      ovenTimeMeanByOperationRollup =
        piecesWithOperationAndExecution
          |> filter(fn: (r) => exists r.data_oven_time)
          |> map(fn: (r) => ({
              _time: r._time,
              _value: r.data_oven_time,
              data_pattern_operation: r.data_pattern_operation
          }))
          |> group(columns: ["data_pattern_operation"])
          |> mean(column: "_value")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "oven_time_mean",
              _value: r._value,
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      
      transferTimeMeanByOperationRollup =
        piecesWithOperationAndExecution
          |> filter(fn: (r) => exists r.data_transfer_time)
          |> map(fn: (r) => ({
              _time: r._time,
              _value: r.data_transfer_time,
              data_pattern_operation: r.data_pattern_operation
          }))
          |> group(columns: ["data_pattern_operation"])
          |> mean(column: "_value")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "transfer_time_mean",
              _value: r._value,
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      
      
      productionRatioOperationRollupProdBins =
        piecesWithOperationAndExecution
          |> map(fn: (r) => ({
              _time: r._time,
              _value: 1.0,
              data_pattern_operation: r.data_pattern_operation
          }))
          |> group(columns: ["data_pattern_operation"])
          |> aggregateWindow(every: binSize, fn: sum, createEmpty: false)
          |> reduce(
              identity: {prod_bins: 0.0},
              fn: (r, accumulator) => ({prod_bins: accumulator.prod_bins + 1.0})
          )
          |> keep(columns: ["data_pattern_operation", "prod_bins"])
      
      productionRatioOperationRollupActiveBins =
        piecesWithOperationAndExecution
          |> group(columns: ["data_pattern_execution_id", "data_pattern_operation"])
          |> sort(columns: ["_time"])
          |> reduce(
              identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
              fn: (r, accumulator) => ({
                points: accumulator.points + 1,
                first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
                last_ns: uint(v: r._time)
              })
          )
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              active_bins: if r.points == 0 then 0.0 else math.ceil(x: float(v: r.last_ns - r.first_ns) / float(v: uint(v: binSize)) + 1.0)
          }))
          |> group(columns: ["data_pattern_operation"])
          |> sum(column: "active_bins")
          |> keep(columns: ["data_pattern_operation", "active_bins"])
      
      productionTimeRatioOperationRollupKeys =
        piecesWithOperation
          |> group(columns: ["data_pattern_operation"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({join_key: "total", data_pattern_operation: r.data_pattern_operation}))
          |> keep(columns: ["join_key", "data_pattern_operation"])
      
      productionTimeRatioByOperationRollup =
        join(tables: {keys: productionTimeRatioOperationRollupKeys, ratio: productionTimeRatioGlobal}, on: ["join_key"])
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "production_time_ratio",
              _value: r.production_time_ratio,
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      meanSurplusOvenTimeVsPatternByOperationRollup =
        piecesWithOperationAndExecution
          |> filter(fn: (r) =>
            exists r.data_oven_time and
            exists r.data_pattern_oven_time
          )
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              surplus_oven_time: if r.data_oven_time > r.data_pattern_oven_time then r.data_oven_time - r.data_pattern_oven_time else 0.0
          }))
          |> group(columns: ["data_pattern_operation"])
          |> mean(column: "surplus_oven_time")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "mean_surplus_oven_time_vs_pattern",
              _value: r.surplus_oven_time,
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      
      cadenceOperationRollupPieceCounts =
        piecesWithOperationAndExecution
          |> group(columns: ["data_pattern_operation"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              total_pieces: float(v: r.data_piece_id)
          }))
          |> keep(columns: ["data_pattern_operation", "total_pieces"])
      
      cadenceOperationRollupActiveMinutes =
        piecesWithOperationAndExecution
          |> group(columns: ["data_pattern_execution_id", "data_pattern_operation"])
          |> sort(columns: ["_time"])
          |> reduce(
              identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
              fn: (r, accumulator) => ({
                points: accumulator.points + 1,
                first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
                last_ns: uint(v: r._time)
              })
          )
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              active_minutes: if r.points == 0 then 0.0 else math.ceil(x: float(v: r.last_ns - r.first_ns) / 60000000000.0 + 1.0)
          }))
          |> group(columns: ["data_pattern_operation"])
          |> sum(column: "active_minutes")
          |> keep(columns: ["data_pattern_operation", "active_minutes"])
      
      averagePiecesPerMinuteByOperationRollup =
        join(tables: {pieces: cadenceOperationRollupPieceCounts, active: cadenceOperationRollupActiveMinutes}, on: ["data_pattern_operation"])
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "average_pieces_per_minute",
              _value: if r.active_minutes <= 0.0 then 0.0 else r.total_pieces / r.active_minutes,
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      
      totalPiecesCountByOperationRollup =
        piecesWithOperation
          |> group(columns: ["data_pattern_operation"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "total_pieces_count",
              _value: float(v: r.data_piece_id),
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      
      patternMatchedRatioByOperationRollup =
        piecesWithOperation
          |> map(fn: (r) => ({
              data_pattern_operation: r.data_pattern_operation,
              total_piece: 1,
              matched_piece: if exists r.data_pattern_execution_id then 1 else 0
          }))
          |> group(columns: ["data_pattern_operation"])
          |> reduce(
              identity: {total_pieces: 0, matched_pieces: 0},
              fn: (r, accumulator) => ({
                total_pieces: accumulator.total_pieces + r.total_piece,
                matched_pieces: accumulator.matched_pieces + r.matched_piece
              })
          )
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "pattern_matched_ratio",
              _value: if r.total_pieces == 0 then 0.0 else float(v: r.matched_pieces) / float(v: r.total_pieces),
              data_pattern_operation: r.data_pattern_operation
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_operation"])
      
      // --------------------------------------------------------------------------
      // Niveau configuration seule : operation null
      // --------------------------------------------------------------------------
      
      struckPiecesCountByConfigurationRollup =
        piecesWithConfigurationAndExecution
          |> group(columns: ["data_pattern_execution_configuration"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "struck_pieces_count",
              _value: float(v: r.data_piece_id),
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      
      conformityRatioByConfigurationRollup =
        piecesWithConfigurationAndExecution
          |> map(fn: (r) => ({
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              total_piece: 1,
              conform_piece:
                if exists r.data_oven_time and exists r.data_pattern_oven_time and exists r.data_pattern_oven_tolerance then
                  if r.data_oven_time >= r.data_pattern_oven_time and r.data_oven_time <= r.data_pattern_oven_time + r.data_pattern_oven_tolerance then 1 else 0
                else 0
          }))
          |> group(columns: ["data_pattern_execution_configuration"])
          |> reduce(
              identity: {total_pieces: 0, conform_pieces: 0},
              fn: (r, accumulator) => ({
                total_pieces: accumulator.total_pieces + r.total_piece,
                conform_pieces: accumulator.conform_pieces + r.conform_piece
              })
          )
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "conformity_ratio",
              _value: if r.total_pieces == 0 then 0.0 else float(v: r.conform_pieces) / float(v: r.total_pieces),
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      
      exceededOvenTimeCountByConfigurationRollup =
        piecesWithConfigurationAndExecution
          |> map(fn: (r) => ({
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              exceeded_oven_piece: if exists r.data_exceeded_oven_time then (if r.data_exceeded_oven_time == 1.0 then 1 else 0) else 0
          }))
          |> group(columns: ["data_pattern_execution_configuration"])
          |> sum(column: "exceeded_oven_piece")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "exceeded_oven_time_count",
              _value: float(v: r.exceeded_oven_piece),
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      
      ovenTimeMeanByConfigurationRollup =
        piecesWithConfigurationAndExecution
          |> filter(fn: (r) => exists r.data_oven_time)
          |> map(fn: (r) => ({
              _time: r._time,
              _value: r.data_oven_time,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> group(columns: ["data_pattern_execution_configuration"])
          |> mean(column: "_value")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "oven_time_mean",
              _value: r._value,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      
      transferTimeMeanByConfigurationRollup =
        piecesWithConfigurationAndExecution
          |> filter(fn: (r) => exists r.data_transfer_time)
          |> map(fn: (r) => ({
              _time: r._time,
              _value: r.data_transfer_time,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> group(columns: ["data_pattern_execution_configuration"])
          |> mean(column: "_value")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "transfer_time_mean",
              _value: r._value,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      
      
      productionRatioConfigurationRollupProdBins =
        piecesWithConfigurationAndExecution
          |> map(fn: (r) => ({
              _time: r._time,
              _value: 1.0,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> group(columns: ["data_pattern_execution_configuration"])
          |> aggregateWindow(every: binSize, fn: sum, createEmpty: false)
          |> reduce(
              identity: {prod_bins: 0.0},
              fn: (r, accumulator) => ({prod_bins: accumulator.prod_bins + 1.0})
          )
          |> keep(columns: ["data_pattern_execution_configuration", "prod_bins"])
      
      productionRatioConfigurationRollupActiveBins =
        piecesWithConfigurationAndExecution
          |> group(columns: ["data_pattern_execution_id", "data_pattern_execution_configuration"])
          |> sort(columns: ["_time"])
          |> reduce(
              identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
              fn: (r, accumulator) => ({
                points: accumulator.points + 1,
                first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
                last_ns: uint(v: r._time)
              })
          )
          |> map(fn: (r) => ({
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              active_bins: if r.points == 0 then 0.0 else math.ceil(x: float(v: r.last_ns - r.first_ns) / float(v: uint(v: binSize)) + 1.0)
          }))
          |> group(columns: ["data_pattern_execution_configuration"])
          |> sum(column: "active_bins")
          |> keep(columns: ["data_pattern_execution_configuration", "active_bins"])
      
      productionTimeRatioConfigurationRollupKeys =
        piecesWithConfiguration
          |> group(columns: ["data_pattern_execution_configuration"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({join_key: "total", data_pattern_execution_configuration: r.data_pattern_execution_configuration}))
          |> keep(columns: ["join_key", "data_pattern_execution_configuration"])
      
      productionTimeRatioByConfigurationRollup =
        join(tables: {keys: productionTimeRatioConfigurationRollupKeys, ratio: productionTimeRatioGlobal}, on: ["join_key"])
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "production_time_ratio",
              _value: r.production_time_ratio,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      meanSurplusOvenTimeVsPatternByConfigurationRollup =
        piecesWithConfigurationAndExecution
          |> filter(fn: (r) =>
            exists r.data_oven_time and
            exists r.data_pattern_oven_time
          )
          |> map(fn: (r) => ({
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              surplus_oven_time: if r.data_oven_time > r.data_pattern_oven_time then r.data_oven_time - r.data_pattern_oven_time else 0.0
          }))
          |> group(columns: ["data_pattern_execution_configuration"])
          |> mean(column: "surplus_oven_time")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "mean_surplus_oven_time_vs_pattern",
              _value: r.surplus_oven_time,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      
      cadenceConfigurationRollupPieceCounts =
        piecesWithConfigurationAndExecution
          |> group(columns: ["data_pattern_execution_configuration"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              total_pieces: float(v: r.data_piece_id)
          }))
          |> keep(columns: ["data_pattern_execution_configuration", "total_pieces"])
      
      cadenceConfigurationRollupActiveMinutes =
        piecesWithConfigurationAndExecution
          |> group(columns: ["data_pattern_execution_id", "data_pattern_execution_configuration"])
          |> sort(columns: ["_time"])
          |> reduce(
              identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
              fn: (r, accumulator) => ({
                points: accumulator.points + 1,
                first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
                last_ns: uint(v: r._time)
              })
          )
          |> map(fn: (r) => ({
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              active_minutes: if r.points == 0 then 0.0 else math.ceil(x: float(v: r.last_ns - r.first_ns) / 60000000000.0 + 1.0)
          }))
          |> group(columns: ["data_pattern_execution_configuration"])
          |> sum(column: "active_minutes")
          |> keep(columns: ["data_pattern_execution_configuration", "active_minutes"])
      
      averagePiecesPerMinuteByConfigurationRollup =
        join(tables: {pieces: cadenceConfigurationRollupPieceCounts, active: cadenceConfigurationRollupActiveMinutes}, on: ["data_pattern_execution_configuration"])
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "average_pieces_per_minute",
              _value: if r.active_minutes <= 0.0 then 0.0 else r.total_pieces / r.active_minutes,
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      
      // Donnees de couverture par configuration calculees avec toutes les pieces
      // portant cette configuration, avec ou sans pattern.
      totalPiecesCountByConfigurationRollup =
        piecesWithConfiguration
          |> group(columns: ["data_pattern_execution_configuration"])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "total_pieces_count",
              _value: float(v: r.data_piece_id),
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      
      patternMatchedRatioByConfigurationRollup =
        piecesWithConfiguration
          |> map(fn: (r) => ({
              data_pattern_execution_configuration: r.data_pattern_execution_configuration,
              total_piece: 1,
              matched_piece: if exists r.data_pattern_execution_id then 1 else 0
          }))
          |> group(columns: ["data_pattern_execution_configuration"])
          |> reduce(
              identity: {total_pieces: 0, matched_pieces: 0},
              fn: (r, accumulator) => ({
                total_pieces: accumulator.total_pieces + r.total_piece,
                matched_pieces: accumulator.matched_pieces + r.matched_piece
              })
          )
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "pattern_matched_ratio",
              _value: if r.total_pieces == 0 then 0.0 else float(v: r.matched_pieces) / float(v: r.total_pieces),
              data_pattern_execution_configuration: r.data_pattern_execution_configuration
          }))
          |> keep(columns: ["_time", "_field", "_value", "data_pattern_execution_configuration"])
      
      // --------------------------------------------------------------------------
      // Niveau total global : operation null + configuration null
      // --------------------------------------------------------------------------
      
      struckPiecesCountTotal =
        piecesWithExecution
          |> group(columns: [])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "struck_pieces_count",
              _value: float(v: r.data_piece_id)
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      
      conformityRatioTotal =
        piecesWithExecution
          |> map(fn: (r) => ({
              total_piece: 1,
              conform_piece:
                if exists r.data_pattern_oven_time and exists r.data_pattern_oven_tolerance and exists r.data_oven_time then
                  if r.data_oven_time >= r.data_pattern_oven_time and r.data_oven_time <= r.data_pattern_oven_time + r.data_pattern_oven_tolerance then 1 else 0
                else 0
          }))
          |> group(columns: [])
          |> reduce(
              identity: {total_pieces: 0, conform_pieces: 0},
              fn: (r, accumulator) => ({
                total_pieces: accumulator.total_pieces + r.total_piece,
                conform_pieces: accumulator.conform_pieces + r.conform_piece
              })
          )
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "conformity_ratio",
              _value: if r.total_pieces == 0 then 0.0 else float(v: r.conform_pieces) / float(v: r.total_pieces)
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      
      exceededOvenTimeCountTotal =
        piecesWithExecution
          |> map(fn: (r) => ({
              exceeded_oven_piece: if exists r.data_exceeded_oven_time then (if r.data_exceeded_oven_time == 1.0 then 1 else 0) else 0
          }))
          |> group(columns: [])
          |> sum(column: "exceeded_oven_piece")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "exceeded_oven_time_count",
              _value: float(v: r.exceeded_oven_piece)
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      
      
      ovenTimeMeanTotal =
        piecesWithExecution
          |> filter(fn: (r) => exists r.data_oven_time)
          |> map(fn: (r) => ({_time: r._time, _value: r.data_oven_time}))
          |> group(columns: [])
          |> mean(column: "_value")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "oven_time_mean",
              _value: r._value
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      
      transferTimeMeanTotal =
        piecesWithExecution
          |> filter(fn: (r) => exists r.data_transfer_time)
          |> map(fn: (r) => ({_time: r._time, _value: r.data_transfer_time}))
          |> group(columns: [])
          |> mean(column: "_value")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "transfer_time_mean",
              _value: r._value
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      
      
      productionRatioTotalProdBins =
        piecesWithExecution
          |> map(fn: (r) => ({_time: r._time, _value: 1.0, join_key: "total"}))
          |> group(columns: ["join_key"])
          |> aggregateWindow(every: binSize, fn: sum, createEmpty: false)
          |> reduce(
              identity: {prod_bins: 0.0},
              fn: (r, accumulator) => ({prod_bins: accumulator.prod_bins + 1.0})
          )
          |> keep(columns: ["join_key", "prod_bins"])
      
      productionRatioTotalActiveBins =
        piecesWithExecution
          |> group(columns: ["data_pattern_execution_id"])
          |> sort(columns: ["_time"])
          |> reduce(
              identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
              fn: (r, accumulator) => ({
                points: accumulator.points + 1,
                first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
                last_ns: uint(v: r._time)
              })
          )
          |> map(fn: (r) => ({
              join_key: "total",
              active_bins: if r.points == 0 then 0.0 else math.ceil(x: float(v: r.last_ns - r.first_ns) / float(v: uint(v: binSize)) + 1.0)
          }))
          |> group(columns: ["join_key"])
          |> sum(column: "active_bins")
          |> keep(columns: ["join_key", "active_bins"])
      
      productionTimeRatioTotal =
        productionTimeRatioGlobal
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "production_time_ratio",
              _value: r.production_time_ratio
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      meanSurplusOvenTimeVsPatternTotal =
        piecesWithExecution
          |> filter(fn: (r) =>
            exists r.data_oven_time and
            exists r.data_pattern_oven_time
          )
          |> map(fn: (r) => ({
              surplus_oven_time: if r.data_oven_time > r.data_pattern_oven_time then r.data_oven_time - r.data_pattern_oven_time else 0.0
          }))
          |> group(columns: [])
          |> mean(column: "surplus_oven_time")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "mean_surplus_oven_time_vs_pattern",
              _value: r.surplus_oven_time
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      
      averagePiecesPerMinuteTotal =
        piecesWithExecution
          |> map(fn: (r) => ({_time: r._time, _value: 1.0}))
          |> group(columns: [])
          |> aggregateWindow(every: 1m, fn: sum, createEmpty: true)
          |> fill(column: "_value", value: 0.0)
          |> reduce(
              identity: {total_pieces: 0.0, total_minutes: 0.0},
              fn: (r, accumulator) => ({
                total_pieces: accumulator.total_pieces + r._value,
                total_minutes: accumulator.total_minutes + 1.0
              })
          )
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "average_pieces_per_minute",
              _value: if r.total_minutes == 0.0 then 0.0 else r.total_pieces / r.total_minutes
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      
      
      totalPiecesCountTotal =
        pieces
          |> group(columns: [])
          |> count(column: "data_piece_id")
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "total_pieces_count",
              _value: float(v: r.data_piece_id)
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      
      patternMatchedRatioTotal =
        pieces
          |> map(fn: (r) => ({
              total_piece: 1,
              matched_piece: if exists r.data_pattern_execution_id then 1 else 0
          }))
          |> group(columns: [])
          |> reduce(
              identity: {total_pieces: 0, matched_pieces: 0},
              fn: (r, accumulator) => ({
                total_pieces: accumulator.total_pieces + r.total_piece,
                matched_pieces: accumulator.matched_pieces + r.matched_piece
              })
          )
          |> map(fn: (r) => ({
              _time: ${stop},
              _field: "pattern_matched_ratio",
              _value: if r.total_pieces == 0 then 0.0 else float(v: r.matched_pieces) / float(v: r.total_pieces)
          }))
          |> keep(columns: ["_time", "_field", "_value"])
      
      
      mainKpis =
        union(tables: [
          // Operation + configuration
          struckPiecesCountByCombination,
          conformityRatioByCombination,
          exceededOvenTimeCountByCombination,
          ovenTimeMeanByCombination,
          transferTimeMeanByCombination,
          productionTimeRatioByCombination,
          meanSurplusOvenTimeVsPatternByCombination,
          averagePiecesPerMinuteByCombination,
      
          // Operation seule, configuration null
          struckPiecesCountByOperationRollup,
          conformityRatioByOperationRollup,
          exceededOvenTimeCountByOperationRollup,
          ovenTimeMeanByOperationRollup,
          transferTimeMeanByOperationRollup,
          productionTimeRatioByOperationRollup,
          meanSurplusOvenTimeVsPatternByOperationRollup,
          averagePiecesPerMinuteByOperationRollup,
      
          // Configuration seule, operation null
          struckPiecesCountByConfigurationRollup,
          conformityRatioByConfigurationRollup,
          exceededOvenTimeCountByConfigurationRollup,
          ovenTimeMeanByConfigurationRollup,
          transferTimeMeanByConfigurationRollup,
          productionTimeRatioByConfigurationRollup,
          meanSurplusOvenTimeVsPatternByConfigurationRollup,
          averagePiecesPerMinuteByConfigurationRollup,
      
          // Total global, operation null + configuration null
          struckPiecesCountTotal,
          conformityRatioTotal,
          exceededOvenTimeCountTotal,
          ovenTimeMeanTotal,
          transferTimeMeanTotal,
          productionTimeRatioTotal,
          meanSurplusOvenTimeVsPatternTotal,
          averagePiecesPerMinuteTotal
        ])
          |> keep(columns: ["_time", "data_pattern_operation", "data_pattern_execution_configuration", "_field", "_value"])
          |> group(columns: [])
          |> pivot(
            rowKey: ["_time", "data_pattern_operation", "data_pattern_execution_configuration"],
            columnKey: ["_field"],
            valueColumn: "_value"
          )
          |> group(columns: [])
      
      globalCoverage =
        union(tables: [
          totalPiecesCountTotal,
          patternMatchedRatioTotal
        ])
          |> keep(columns: ["_time", "_field", "_value"])
          |> group(columns: [])
          |> pivot(
            rowKey: ["_time"],
            columnKey: ["_field"],
            valueColumn: "_value"
          )
          |> group(columns: [])
      
      join(tables: {kpis: mainKpis, coverage: globalCoverage}, on: ["_time"])
        |> sort(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
        |> yield(name: "kpis_operation_configuration_cube_matched_only_with_global_coverage_wide_v7")
    `,
  }[queryName];
};

export const getLISITRSQuery = (start, stop) => {
  return `
    import "math"
    import "internal/debug"
    
    binSize = 15m
    bucketName = "engine"
    measurementName = "events"
    
    // Temps de prod / temps de reference - Par jour.
    //
    // Regles :
    // - Les pieces sans data_pattern_execution_id sont exclues partout.
    // - Pour operation + configuration :
    //     ratio = bins productifs du groupe / bins actifs des executions de pattern du groupe
    // - Pour operation + null :
    //     ratio = bins productifs de l'operation / bins actifs des executions de pattern de l'operation
    // - Pour null + configuration :
    //     ratio = bins productifs de la configuration / bins actifs des executions de pattern de la configuration
    // - Pour null + null :
    //     ratio = bins productifs avec pattern / bins actifs de tous les patterns
    //
    // Le temps actif d'une execution est estime de la premiere a la derniere piece observee
    // dans la journee, avec un minimum de 1 bin par execution observee.
    
    pieces =
      from(bucket: bucketName)
        |> range(start: ${start}, stop: ${stop})
        |> filter(fn: (r) => r._measurement == measurementName)
        |> filter(fn: (r) => r._field == "data_piece_id")
        |> keep(columns: ["_time"])
        |> map(fn: (r) => ({
            _time: r._time,
            _value: 1
        }))
    
    executions =
      from(bucket: bucketName)
        |> range(start: ${start}, stop: ${stop})
        |> filter(fn: (r) => r._measurement == measurementName)
        |> filter(fn: (r) => r._field == "data_pattern_execution_id")
        |> filter(fn: (r) => exists r._value)
        |> keep(columns: ["_time", "_value"])
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_id: string(v: r._value)
        }))
    
    operations =
      from(bucket: bucketName)
        |> range(start: ${start}, stop: ${stop})
        |> filter(fn: (r) => r._measurement == measurementName)
        |> filter(fn: (r) => r._field == "data_pattern_operation")
        |> filter(fn: (r) => exists r._value and r._value != "None")
        |> keep(columns: ["_time", "_value"])
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_operation: string(v: r._value)
        }))
    
    configurations =
      from(bucket: bucketName)
        |> range(start: ${start}, stop: ${stop})
        |> filter(fn: (r) => r._measurement == measurementName)
        |> filter(fn: (r) => r._field == "data_pattern_execution_configuration")
        |> filter(fn: (r) => exists r._value and r._value != "None")
        |> keep(columns: ["_time", "_value"])
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_configuration: string(v: r._value)
        }))
    
    piecesWithExecution =
      join(tables: {piece: pieces, execution: executions}, on: ["_time"])
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_id: r.data_pattern_execution_id,
            _value: 1
        }))
    
    piecesWithOperation =
      join(tables: {piece: piecesWithExecution, operation: operations}, on: ["_time"])
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_id: r.data_pattern_execution_id,
            data_pattern_operation: r.data_pattern_operation,
            _value: 1
        }))
    
    piecesWithConfiguration =
      join(tables: {piece: piecesWithExecution, configuration: configurations}, on: ["_time"])
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_id: r.data_pattern_execution_id,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            _value: 1
        }))
    
    piecesWithOperationAndConfiguration =
      join(tables: {operation: piecesWithOperation, configuration: configurations}, on: ["_time"])
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_id: r.data_pattern_execution_id,
            data_pattern_operation: r.data_pattern_operation,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            _value: 1
        }))
    
    // --------------------------------------------------------------------------
    // Combinaison exacte operation + configuration
    // --------------------------------------------------------------------------
    
    prodBinsByCombination =
      piecesWithOperationAndConfiguration
        |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
        |> aggregateWindow(every: binSize, fn: count, createEmpty: false)
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_operation: r.data_pattern_operation,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            _field: "prod_bins",
            _value: 1.0
        }))
        |> aggregateWindow(every: 1d, fn: sum, createEmpty: true)
        |> fill(column: "_value", value: 0.0)
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_operation: r.data_pattern_operation,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            _field: "prod_bins",
            _value: r._value
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_operation",
            "data_pattern_execution_configuration"
        ])
    
    activeBinsByExecutionCombinationByDay =
      piecesWithOperationAndConfiguration
        |> group(columns: [
            "data_pattern_execution_id",
            "data_pattern_operation",
            "data_pattern_execution_configuration"
        ])
        |> sort(columns: ["_time"])
        |> window(every: 1d)
        |> reduce(
            identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
            fn: (r, accumulator) => ({
              points: accumulator.points + 1,
              first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
              last_ns: uint(v: r._time)
            })
        )
        |> map(fn: (r) => ({
            _time: r._stop,
            data_pattern_operation: r.data_pattern_operation,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            active_bins: if r.points == 0 then 0.0 else math.ceil(
                x: float(v: r.last_ns - r.first_ns) / float(v: uint(v: binSize)) + 1.0
            )
        }))
        |> keep(columns: [
            "_time",
            "data_pattern_operation",
            "data_pattern_execution_configuration",
            "active_bins"
        ])
    
    activeBinsByCombination =
      activeBinsByExecutionCombinationByDay
        |> group(columns: [
            "data_pattern_operation",
            "data_pattern_execution_configuration",
            "_time"
        ])
        |> sum(column: "active_bins")
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_operation: r.data_pattern_operation,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            _field: "reference_bins",
            _value: r.active_bins
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_operation",
            "data_pattern_execution_configuration"
        ])
    
    ratioByCombination =
      union(tables: [prodBinsByCombination, activeBinsByCombination])
        |> group(columns: ["data_pattern_operation", "data_pattern_execution_configuration"])
        |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_operation: r.data_pattern_operation,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            prod_bins: if exists r.prod_bins then r.prod_bins else 0.0,
            reference_bins: if exists r.reference_bins then r.reference_bins else 0.0
        }))
        |> map(fn: (r) => ({
            _time: r._time,
            _field: "production_time_ratio_1d_bins_pattern_time",
            _value: if r.reference_bins <= 0.0 then 0.0 else r.prod_bins / r.reference_bins,
            data_pattern_operation: r.data_pattern_operation,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            prod_bins: r.prod_bins,
            reference_bins: r.reference_bins
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_operation",
            "data_pattern_execution_configuration",
            "prod_bins",
            "reference_bins"
        ])
    
    // --------------------------------------------------------------------------
    // Rollup operation : toutes configurations
    // --------------------------------------------------------------------------
    
    prodBinsByOperation =
      piecesWithOperation
        |> group(columns: ["data_pattern_operation"])
        |> aggregateWindow(every: binSize, fn: count, createEmpty: false)
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_operation: r.data_pattern_operation,
            _field: "prod_bins",
            _value: 1.0
        }))
        |> aggregateWindow(every: 1d, fn: sum, createEmpty: true)
        |> fill(column: "_value", value: 0.0)
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_operation: r.data_pattern_operation,
            _field: "prod_bins",
            _value: r._value
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_operation"
        ])
    
    activeBinsByExecutionOperationByDay =
      piecesWithOperation
        |> group(columns: ["data_pattern_execution_id", "data_pattern_operation"])
        |> sort(columns: ["_time"])
        |> window(every: 1d)
        |> reduce(
            identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
            fn: (r, accumulator) => ({
              points: accumulator.points + 1,
              first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
              last_ns: uint(v: r._time)
            })
        )
        |> map(fn: (r) => ({
            _time: r._stop,
            data_pattern_operation: r.data_pattern_operation,
            active_bins: if r.points == 0 then 0.0 else math.ceil(
                x: float(v: r.last_ns - r.first_ns) / float(v: uint(v: binSize)) + 1.0
            )
        }))
        |> keep(columns: [
            "_time",
            "data_pattern_operation",
            "active_bins"
        ])
    
    activeBinsByOperation =
      activeBinsByExecutionOperationByDay
        |> group(columns: ["data_pattern_operation", "_time"])
        |> sum(column: "active_bins")
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_operation: r.data_pattern_operation,
            _field: "reference_bins",
            _value: r.active_bins
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_operation"
        ])
    
    ratioByOperationRollup =
      union(tables: [prodBinsByOperation, activeBinsByOperation])
        |> group(columns: ["data_pattern_operation"])
        |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_operation: r.data_pattern_operation,
            prod_bins: if exists r.prod_bins then r.prod_bins else 0.0,
            reference_bins: if exists r.reference_bins then r.reference_bins else 0.0
        }))
        |> map(fn: (r) => ({
            _time: r._time,
            _field: "production_time_ratio_1d_bins_pattern_time",
            _value: if r.reference_bins <= 0.0 then 0.0 else r.prod_bins / r.reference_bins,
            data_pattern_operation: r.data_pattern_operation,
            data_pattern_execution_configuration: debug.null(type: "string"),
            prod_bins: r.prod_bins,
            reference_bins: r.reference_bins
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_operation",
            "data_pattern_execution_configuration",
            "prod_bins",
            "reference_bins"
        ])
    
    // --------------------------------------------------------------------------
    // Rollup configuration : toutes operations
    // --------------------------------------------------------------------------
    
    prodBinsByConfiguration =
      piecesWithConfiguration
        |> group(columns: ["data_pattern_execution_configuration"])
        |> aggregateWindow(every: binSize, fn: count, createEmpty: false)
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            _field: "prod_bins",
            _value: 1.0
        }))
        |> aggregateWindow(every: 1d, fn: sum, createEmpty: true)
        |> fill(column: "_value", value: 0.0)
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            _field: "prod_bins",
            _value: r._value
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_execution_configuration"
        ])
    
    activeBinsByExecutionConfigurationByDay =
      piecesWithConfiguration
        |> group(columns: ["data_pattern_execution_id", "data_pattern_execution_configuration"])
        |> sort(columns: ["_time"])
        |> window(every: 1d)
        |> reduce(
            identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
            fn: (r, accumulator) => ({
              points: accumulator.points + 1,
              first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
              last_ns: uint(v: r._time)
            })
        )
        |> map(fn: (r) => ({
            _time: r._stop,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            active_bins: if r.points == 0 then 0.0 else math.ceil(
                x: float(v: r.last_ns - r.first_ns) / float(v: uint(v: binSize)) + 1.0
            )
        }))
        |> keep(columns: [
            "_time",
            "data_pattern_execution_configuration",
            "active_bins"
        ])
    
    activeBinsByConfiguration =
      activeBinsByExecutionConfigurationByDay
        |> group(columns: ["data_pattern_execution_configuration", "_time"])
        |> sum(column: "active_bins")
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            _field: "reference_bins",
            _value: r.active_bins
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_execution_configuration"
        ])
    
    ratioByConfigurationRollup =
      union(tables: [prodBinsByConfiguration, activeBinsByConfiguration])
        |> group(columns: ["data_pattern_execution_configuration"])
        |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
        |> map(fn: (r) => ({
            _time: r._time,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            prod_bins: if exists r.prod_bins then r.prod_bins else 0.0,
            reference_bins: if exists r.reference_bins then r.reference_bins else 0.0
        }))
        |> map(fn: (r) => ({
            _time: r._time,
            _field: "production_time_ratio_1d_bins_pattern_time",
            _value: if r.reference_bins <= 0.0 then 0.0 else r.prod_bins / r.reference_bins,
            data_pattern_operation: debug.null(type: "string"),
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            prod_bins: r.prod_bins,
            reference_bins: r.reference_bins
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_operation",
            "data_pattern_execution_configuration",
            "prod_bins",
            "reference_bins"
        ])
    
    // --------------------------------------------------------------------------
    // Rollup total global : toutes operations, toutes configurations
    // --------------------------------------------------------------------------
    // Ici :
    // - numerateur = bins avec au moins une piece ayant un pattern_execution_id
    // - reference = bins actifs de tous les patterns
    
    prodBinsTotal =
      piecesWithExecution
        |> group(columns: [])
        |> aggregateWindow(every: binSize, fn: count, createEmpty: false)
        |> map(fn: (r) => ({
            _time: r._time,
            _field: "prod_bins",
            _value: 1.0
        }))
        |> aggregateWindow(every: 1d, fn: sum, createEmpty: true)
        |> fill(column: "_value", value: 0.0)
        |> map(fn: (r) => ({
            _time: r._time,
            _field: "prod_bins",
            _value: r._value
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value"
        ])
    
    activeBinsByExecutionTotalByDay =
      piecesWithExecution
        |> group(columns: ["data_pattern_execution_id"])
        |> sort(columns: ["_time"])
        |> window(every: 1d)
        |> reduce(
            identity: {points: 0, first_ns: uint(v: 0), last_ns: uint(v: 0)},
            fn: (r, accumulator) => ({
              points: accumulator.points + 1,
              first_ns: if accumulator.points == 0 then uint(v: r._time) else accumulator.first_ns,
              last_ns: uint(v: r._time)
            })
        )
        |> map(fn: (r) => ({
            _time: r._stop,
            active_bins: if r.points == 0 then 0.0 else math.ceil(
                x: float(v: r.last_ns - r.first_ns) / float(v: uint(v: binSize)) + 1.0
            )
        }))
        |> keep(columns: [
            "_time",
            "active_bins"
        ])
    
    activeBinsTotal =
      activeBinsByExecutionTotalByDay
        |> group(columns: ["_time"])
        |> sum(column: "active_bins")
        |> map(fn: (r) => ({
            _time: r._time,
            _field: "reference_bins",
            _value: r.active_bins
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value"
        ])
    
    ratioTotalRollup =
      union(tables: [prodBinsTotal, activeBinsTotal])
        |> group(columns: [])
        |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
        |> map(fn: (r) => ({
            _time: r._time,
            prod_bins: if exists r.prod_bins then r.prod_bins else 0.0,
            reference_bins: if exists r.reference_bins then r.reference_bins else 0.0
        }))
        |> map(fn: (r) => ({
            _time: r._time,
            _field: "production_time_ratio_1d_bins_pattern_time",
            _value: if r.reference_bins <= 0.0 then 0.0 else r.prod_bins / r.reference_bins,
            data_pattern_operation: debug.null(type: "string"),
            data_pattern_execution_configuration: debug.null(type: "string"),
            prod_bins: r.prod_bins,
            reference_bins: r.reference_bins
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_operation",
            "data_pattern_execution_configuration",
            "prod_bins",
            "reference_bins"
        ])
    
    // --------------------------------------------------------------------------
    // Nouveau principe : ratio global journalier recopie sur chaque combinaison
    // --------------------------------------------------------------------------
    // Numerateur = nombre de bins de production contenant au moins une piece,
    //              avec ou sans pattern.
    // Denominateur = nombre total de bins de la journee / plage selectionnee.
    // Ce ratio est volontairement global : il est ensuite rattache a chaque
    // combinaison/rollup observe pour conserver une ligne par combinaison.
    
    globalProductionTimeRatioByDay =
      pieces
        |> group(columns: [])
        |> aggregateWindow(every: binSize, fn: count, createEmpty: true)
        |> map(fn: (r) => ({
            _time: r._time,
            prod_bin: if r._value > 0 then 1.0 else 0.0,
            total_bin: 1.0
        }))
        |> window(every: 1d)
        |> reduce(
            identity: {prod_bins: 0.0, reference_bins: 0.0},
            fn: (r, accumulator) => ({
              prod_bins: accumulator.prod_bins + r.prod_bin,
              reference_bins: accumulator.reference_bins + r.total_bin
            })
        )
        |> map(fn: (r) => ({
            _time: r._stop,
            _field: "production_time_ratio_1d_bins_total_day",
            _value: if r.reference_bins <= 0.0 then 0.0 else r.prod_bins / r.reference_bins,
            prod_bins: r.prod_bins,
            reference_bins: r.reference_bins
        }))
        |> keep(columns: ["_time", "_field", "_value", "prod_bins", "reference_bins"])
    
    ratioRows =
      union(tables: [
          ratioByCombination,
          ratioByOperationRollup,
          ratioByConfigurationRollup,
          ratioTotalRollup
        ])
        |> group(columns: [])
        |> keep(columns: [
            "_time",
            "data_pattern_operation",
            "data_pattern_execution_configuration"
        ])
    
    globalProductionTimeRatioByDayGrouped =
      globalProductionTimeRatioByDay
        |> group(columns: [])
    
    ratioRowsWithGlobalProductionTimeRatio =
      join(tables: {rows: ratioRows, ratio: globalProductionTimeRatioByDayGrouped}, on: ["_time"])
        |> map(fn: (r) => ({
            _time: r._time,
            _field: r._field,
            _value: r._value,
            data_pattern_operation: r.data_pattern_operation,
            data_pattern_execution_configuration: r.data_pattern_execution_configuration,
            prod_bins: r.prod_bins,
            reference_bins: r.reference_bins
        }))
        |> keep(columns: [
            "_time",
            "_field",
            "_value",
            "data_pattern_operation",
            "data_pattern_execution_configuration",
            "prod_bins",
            "reference_bins"
        ])
    
    ratioRowsWithGlobalProductionTimeRatio
      |> group(columns: [
          "data_pattern_operation",
          "data_pattern_execution_configuration"
      ])
      |> sort(columns: ["_time"])
      |> yield(name: "production_time_ratio_1d_operation_configuration_cube_bins_total_day")
  `;
};

const START_SHIFT_1 = 5;
const START_SHIFT_2 = 13;
const START_SHIFT_3 = 21;

const getShiftBounds = (date) => {
  const hour = date.getHours();

  const start = new Date(date);
  const stop = new Date(date);

  if (hour >= START_SHIFT_1 && hour < START_SHIFT_2) {
    start.setHours(START_SHIFT_1, 0, 0, 0);
    stop.setHours(START_SHIFT_2, 0, 0, 0);
  } else if (hour >= START_SHIFT_2 && hour < START_SHIFT_3) {
    start.setHours(START_SHIFT_2, 0, 0, 0);
    stop.setHours(START_SHIFT_3, 0, 0, 0);
  } else if (hour >= START_SHIFT_3) {
    start.setHours(START_SHIFT_3, 0, 0, 0);

    stop.setDate(stop.getDate() + 1);
    stop.setHours(START_SHIFT_1, 0, 0, 0);
  } else {
    start.setDate(start.getDate() - 1);
    start.setHours(START_SHIFT_3, 0, 0, 0);

    stop.setHours(START_SHIFT_1, 0, 0, 0);
  }

  return { start, stop };
};

const getCurrentShift = (delta_days = 0, now = new Date()) => {
  const date = new Date(now);
  date.setDate(date.getDate() + delta_days);

  const { start, stop } = getShiftBounds(date);

  return { start: start.getTime(), stop: stop.getTime() };
};

const getShiftUntilNow = (delta_days = 0, now = new Date()) => {
  const date = new Date(now);
  const compareDate = new Date(now);

  compareDate.setDate(compareDate.getDate() + delta_days);

  const { start } = getShiftBounds(compareDate);

  const stop = new Date(compareDate);
  stop.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());

  return { start: start.getTime(), stop: stop.getTime() };
};

const getPreviousShift = (now = new Date()) => {
  const { start } = getShiftBounds(new Date(now));

  const previousShiftDate = new Date(start);
  previousShiftDate.setMilliseconds(previousShiftDate.getMilliseconds() - 1);

  const previousShift = getShiftBounds(previousShiftDate);

  return { start: previousShift.start.getTime(), stop: previousShift.stop.getTime() };
};

const getCurrentDay = (delta_days = 0, now = new Date()) => {
  const date = new Date(now);
  date.setDate(date.getDate() + delta_days);

  const start = new Date(date);
  start.setHours(0, 0, 0, 0);

  const stop = new Date(start);
  stop.setDate(stop.getDate() + 1);

  return { start: start.getTime(), stop: stop.getTime() };
};

const getCurrentWeek = (delta_weeks = 0, now = new Date()) => {
  const date = new Date(now);
  date.setDate(date.getDate() + delta_weeks * 7);

  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const start = new Date(date);
  start.setDate(date.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);

  const stop = new Date(start);
  stop.setDate(start.getDate() + 7);

  return { start: start.getTime(), stop: stop.getTime() };
};

const getCurrentMonth = (delta_months = 0, now = new Date()) => {
  const start = new Date(now);
  start.setMonth(start.getMonth() + delta_months, 1);
  start.setHours(0, 0, 0, 0);

  const stop = new Date(start);
  stop.setMonth(stop.getMonth() + 1);

  return { start: start.getTime(), stop: stop.getTime() };
};

const getMonthUntilNow = (delta_months = 0, now = new Date()) => {
  const start = new Date(now);
  start.setMonth(start.getMonth() + delta_months, 1);
  start.setHours(0, 0, 0, 0);

  const stop = new Date(now);

  if (delta_months !== 0) {
    stop.setMonth(stop.getMonth() + delta_months);
  }

  return { start: start.getTime(), stop: stop.getTime() };
};

export const LISI_COMPARE_CRITERIA = [
  {id: 1, label: 'LE TRS', value: 'production_time_request'}
  // {id: 1, label: 'LE TEMPS DE CYCLE', value: 'cycle_time'},
  // {id: 2, label: 'LE TRS', value: 'trs'},

];

export const LISI_COMPARE_SOURCE = [
  { id: 1, label: 'L\'ÉQUIPE ACTUELLE', ...getCurrentShift() },
  { id: 2, label: 'LA JOURNÉE ACTUELLE', ...getCurrentDay() },
  { id: 3, label: 'LA SEMAINE ACTUELLE', ...getCurrentWeek() },
  { id: 4, label: 'LE MOIS ACTUEL', ...getCurrentMonth() },
];

export const LISI_COMPARE_TO = [
  { id: 1, label: 'LA MÊME ÉQUIPE, HIER', ...getCurrentShift(-1) },
  { id: 2, label: 'LA MÊME ÉQUIPE, HIER, JUSQU\'À LA MÊME HEURE', ...getShiftUntilNow(-1) },
  { id: 3, label: 'L\'ÉQUIPE PRÉCÉDENTE', ...getPreviousShift() },
  { id: 4, label: 'LE JOUR PRÉCÉDENT', ...getCurrentDay(-1) },
  { id: 5, label: 'LE MÊME JOUR, LA SEMAINE DERNIÈRE', ...getCurrentDay(-7) },
  { id: 6, label: 'LA SEMAINE DERNIÈRE', ...getCurrentWeek(-1) },
  { id: 7, label: 'LA SEMAINE ACTUELLE', ...getCurrentWeek() },
  { id: 8, label: 'LE MOIS DERNIER', ...getCurrentMonth(-1) },
  { id: 9, label: 'LE MOIS DERNIER, JUSQU\'À LA MÊME DATE', ...getMonthUntilNow(-1) },
  { id: 10, label: 'LE MOIS ACTUEL', ...getCurrentMonth() },
];

export const LISI_OPERATIONS = [
  { id: 0, label: 'Toutes les opérations', value: 'all' },
  { id: 1, label: 'Calibrage', value: 'Calibrage' },
  { id: 2, label: 'Calibrage a chaud', value: 'Calibrage a chaud' },
  { id: 3, label: 'Matricage ebauche', value: 'Matricage ebauche' },
  { id: 4, label: 'Matricage ebauche + eb', value: 'Matricage ebauche + eb' },
  { id: 5, label: 'Aplatissage', value: 'Aplatissage' },
  { id: 6, label: 'Repartition + ebavurage', value: 'Repartition + ebavurage' },
  { id: 7, label: 'Matricage finition', value: 'Matricage finition' },
  { id: 8, label: 'Matricage fini', value: 'Matricage fini' },
  { id: 9, label: 'Fini', value: 'Fini' },
  { id: 10, label: 'Matricage + eb', value: 'Matricage + eb' },
  { id: 11, label: 'Ebauche', value: 'Ebauche' },
];

export const LISI_CONFIGURATIONS = [
  { id: 0, label: 'Toutes les configurations', value: 'all' },
  { id: 1, label: 'A1', value: 'A1' },
  { id: 2, label: 'A2', value: 'A2' },
];